import { APP_INITIALIZER, ApplicationConfig } from '@angular/core'
import { TitleCasePipe } from '@angular/common'
import { provideRouter } from '@angular/router'
import { HTTP_INTERCEPTORS, withInterceptorsFromDi, provideHttpClient } from '@angular/common/http'
import { provideAnimations } from '@angular/platform-browser/animations'
import {
   BrowserCacheLocation,
   IPublicClientApplication,
   LogLevel,
   PublicClientApplication
} from '@azure/msal-browser'
import {
   MsalInterceptor,
   MSAL_INSTANCE,
   MsalInterceptorConfiguration,
   MsalGuardConfiguration,
   MSAL_GUARD_CONFIG,
   MSAL_INTERCEPTOR_CONFIG,
   MsalService,
   MsalGuard,
   MsalBroadcastService,
   ProtectedResourceScopes
} from '@azure/msal-angular'
import { B2C_Authority_URL, b2cInteractionType, b2cPolicies, defaultB2CAuthority } from '@appShared/b2c-policies'
import { SITE_DIRECTORY_ROUTES } from '@appShared/site-directory.routes'
import { APP_ROUTES } from '@app/app.routes'
import { provideEnvironmentNgxMask } from 'ngx-mask'
import { provideSpinnerConfig } from "ngx-spinner"
import {
   AppConfigProvider,
   HttpRequestInterceptor,
   JQUERY_TOKEN,
   MOMENT_TOKEN
} from '@appShared/services'
import { provideToastr } from 'ngx-toastr'
import { environment } from '@appEnvironments/environment'
import { GUID_, guids } from './shared/guids'

/* eslint-disable @typescript-eslint/dot-notation */
const moment = window['moment']
const jQuery = window['$']
//const hybridWebView = window['HybridWebView']
/* eslint-enable @typescript-eslint/dot-notation */

/* Window.sw_portal and the Date helpers are declared in src/app/global.d.ts */
window.sw_portal = window.sw_portal || { api_history: [], errors: [] }

export function loggerCallback(logLevel: LogLevel, message: string) {
   switch (logLevel) {
      case LogLevel.Error:
         console.error(`MSAL loggerCallback(Error): ${message}`)
         return
      case LogLevel.Info:
         console.info(`MSAL loggerCallback(Info): ${message}`)
         return
      case LogLevel.Verbose:
         console.debug(`msMSALal loggerCallback(Verbose): ${message}`)
         return
      case LogLevel.Warning:
         console.warn(`MSAL loggerCallback(Warning): ${message}`)
         return
      default:
         console.log(`MSAL loggerCallback: ${message}`)
         return
   }
}

export function MSALInstanceFactory(): IPublicClientApplication {
   const apiConfig = environment.apiConfig

   return new PublicClientApplication({
      auth: {
         /* guid below used to obfuscate the clientId in PROD */
         clientId: apiConfig.msalConfig.auth[guids.get(GUID_.B8C119E38553467B8ED361CB1AC821C5)],
         authority: defaultB2CAuthority.authority,
         knownAuthorities: [b2cPolicies.authorityDomain],
         redirectUri: `/${SITE_DIRECTORY_ROUTES.auth.baseUri}`,
         postLogoutRedirectUri: '/',
         navigateToLoginRequestUrl: false
      },
      cache: {
         cacheLocation: BrowserCacheLocation.LocalStorage,
      },
      system: {
         allowPlatformBroker: false, // Disables WAM Broker
         loggerOptions: {
            loggerCallback,
            logLevel: LogLevel.Info,
            piiLoggingEnabled: apiConfig.piiLoggingEnabled,
         },
      },
   })
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
   const apiConfig = environment.apiConfig
   const endpointUri = apiConfig.endpointUri
   const scopes = [...apiConfig.scopes]

   const protectedResourceMap =
      new Map<string, Array<string | ProtectedResourceScopes> | null>()
   protectedResourceMap.set(endpointUri, [
      // All GET method endpoints matching the ~/api (root) pattern should allow anonymous requests,
      //  w/protected exceptions to that rule (specific GET endpoints) set below...
      //{ httpMethod: 'GET', scopes },

      // Send a bearer token in the Authorization header + scopes for protected endpoints,
      //  enforcing authorized requests, generally, for the following HTTP methods:
      //{ httpMethod: 'POST', scopes },
      { httpMethod: 'PUT', scopes },
      { httpMethod: 'DELETE', scopes },
      { httpMethod: 'PATCH', scopes }
   ])

   protectedResourceMap.set(`${endpointUri}/dealer/send-password-reset-voucher`, null/* exclude endpoint */);
   protectedResourceMap.set(`${endpointUri}/dealer`, [
      { httpMethod: 'GET', scopes },
      { httpMethod: 'POST', scopes }
   ])

   protectedResourceMap.set(`${endpointUri}/merchandise-returns`, [
      { httpMethod: 'GET', scopes }
   ])

   protectedResourceMap.set(`${endpointUri}/profiles`, [
      { httpMethod: 'GET', scopes }
   ])

   protectedResourceMap.set(`${endpointUri}/sales-school-registration`, [
      { httpMethod: 'GET', scopes },
      { httpMethod: 'POST', scopes }
   ])

   return {
      interactionType: b2cInteractionType,
      protectedResourceMap,
      authRequest: (msalService, httpReq, originalAuthRequest) => {
         return {
            ...originalAuthRequest,
            authority: `${B2C_Authority_URL}/${originalAuthRequest.account?.tenantId ?? apiConfig.userFlows.defaultSignIn}`
         }
      }
   }
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
   return {
      interactionType: b2cInteractionType,
      authRequest: {
         scopes: [...environment.apiConfig.scopes],
      },
      loginFailedRoute: `/${SITE_DIRECTORY_ROUTES.loginFailed.baseUri}`
   }
}


export const appConfig: ApplicationConfig = {
   providers: [
      provideAnimations(),
      provideToastr(),
      provideHttpClient(withInterceptorsFromDi()),
      provideRouter(APP_ROUTES),
      {
         provide: APP_INITIALIZER,
         useFactory: (appConfigProvider: AppConfigProvider) => {
            return () => appConfigProvider.init()
         },
         multi: true,
         deps: [AppConfigProvider]
      },
      {
         provide: Window,
         useValue: window
      },
      {
         provide: HTTP_INTERCEPTORS,
         useClass: HttpRequestInterceptor,
         multi: true
      },
      {
         provide: HTTP_INTERCEPTORS,
         useClass: MsalInterceptor,
         multi: true,
      },
      {
         provide: MSAL_INSTANCE,
         useFactory: MSALInstanceFactory,
      },
      {
         provide: MSAL_GUARD_CONFIG,
         useFactory: MSALGuardConfigFactory,
      },
      {
         provide: MSAL_INTERCEPTOR_CONFIG,
         useFactory: MSALInterceptorConfigFactory,
      },
      MsalService,
      MsalGuard,
      MsalBroadcastService,
      TitleCasePipe,
      { provide: MOMENT_TOKEN, useValue: moment },
      { provide: JQUERY_TOKEN, useValue: jQuery },
      provideEnvironmentNgxMask(),
      provideSpinnerConfig({ type: 'ball-scale-multiple' })
   ]
}

Date.prototype.stdTimezoneOffset = function (): number {
   const jan = new Date(this.getFullYear(), 0, 1)
   const jul = new Date(this.getFullYear(), 6, 1)
   return Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset())
}

Date.prototype.isDstObserved = function (): boolean {
   return this.getTimezoneOffset() < this.stdTimezoneOffset()
}
